import { useApolloClient, useQuery, useLazyQuery } from '@apollo/client/react';
import { useContext, useState } from 'react';
import {
	type EcrmOrderFilter,
	type ProjectDetailsPageQuery,
	type ProjectDetailsPageQueryVariables,
	type ProjectOrdersQuery,
	type ProjectOrdersQueryVariables,
	type ProjectReturnsQuery,
	type ProjectReturnsQueryVariables
} from '../../__generated__/graphql-client-types';
import { ProjectsTourContext } from '../../contexts/projects/projects-tour/projects-tour.context';
import { isProject, isProjectNotFound } from '../../helpers/project/project.helper';
import { PROJECT_DETAILS_PAGE, PROJECT_ORDERS, PROJECT_RETURNS } from '../../queries/project/project-details.queries';
import { type ProjectDetailsData, type ProjectOrder, type ProjectReturn } from '../../types/project.types';
import { useEmployee } from '../apollo/employee/employee.hooks';
import { useRouterLocation } from '../routing/location.hook';
import { useRouteParams } from '../routing/params.hook';

export const useProjectId = () => {
	const { projectId } = useRouteParams<{ projectId: string }>();
	const { search } = useRouterLocation();
	const queryParams = new URLSearchParams(search);
	const qspProjectId = queryParams.get('projectId') || '';
	return parseInt(projectId || qspProjectId, 10);
};

export const useProjectDetails = (cacheFirst = true) => {
	const projectId = useProjectId();
	const { data, loading, refetch } = useQuery<ProjectDetailsPageQuery, ProjectDetailsPageQueryVariables>(PROJECT_DETAILS_PAGE, {
		variables: { projectId },
		fetchPolicy: cacheFirst ? 'cache-first' : 'cache-and-network',
		skip: !projectId
	});
	const result = data?.projectById;
	return {
		project: isProject(result) ? result : undefined,
		loading,
		notFound: isProjectNotFound(result),
		refetch
	};
};

export const useHasEditPermissions = () => {
	const { project } = useProjectDetails();
	const { employee } = useEmployee();
	const isInternal = employee.isAuthenticated;
	const isLocked = project?.isLocked && !isInternal;
	const {
		currentStep: { currentStepIndex }
	} = useContext(ProjectsTourContext);

	return (Boolean(project?.permissions.editProject) && !isLocked) || currentStepIndex >= 0;
};

export const useFetchProjectData = () => {
	return useLazyQuery<ProjectDetailsPageQuery, ProjectDetailsPageQueryVariables>(PROJECT_DETAILS_PAGE);
};

type PartialProjectCachedData = {
	projectById: Partial<ProjectDetailsData>;
};

/**
 * useUpdateProjectData - updates project data in cache
 */
export const useUpdateProjectData = () => {
	const projectId = useProjectId();
	const { project } = useProjectDetails();
	const client = useApolloClient();
	return (data: PartialProjectCachedData['projectById']) => {
		client.writeQuery<PartialProjectCachedData, ProjectDetailsPageQueryVariables>({
			query: PROJECT_DETAILS_PAGE,
			data: { projectById: { ...project, ...data } },
			variables: { projectId }
		});
	};
};

const getOrdersReturnsInput = (projectId: number | undefined): ProjectOrdersQueryVariables['input'] => ({
	searchType: projectId ? 'PROJECT' : '',
	searchValue: String(projectId || ''),
	dateSortOrder: 'DESC',
	status: '',
	pagination: {
		page: 1,
		pageSize: 10,
		useInfiniteScroll: true
	}
});

const loadMoreOrdersReturns = (
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
	fetchMore: ({ variables }: { variables: ProjectOrdersQueryVariables }) => void,
	inputParams: EcrmOrderFilter
) => {
	setCurrentPage((previousPage) => {
		void fetchMore({
			variables: {
				input: {
					...inputParams,
					pagination: {
						page: previousPage + 1,
						pageSize: 10,
						useInfiniteScroll: true
					}
				}
			}
		});

		return previousPage + 1;
	});
};

type ProjectOrdersResult = {
	loading: boolean;
	orders: ProjectOrder[];
	loadMore: () => void;
};

/**
 * Gets orders for a project
 *
 * @param projectId - filter orders to those attached to this project id
 */
export const useProjectOrders = (projectId?: number): ProjectOrdersResult => {
	const [, setCurrentPage] = useState(1);

	const inputParams: ProjectOrdersQueryVariables['input'] = getOrdersReturnsInput(projectId);

	const { data, loading, fetchMore } = useQuery<ProjectOrdersQuery, ProjectOrdersQueryVariables>(PROJECT_ORDERS, {
		variables: {
			input: inputParams
		}
	});

	const loadMore = () => loadMoreOrdersReturns(setCurrentPage, fetchMore, inputParams);

	return {
		orders: data?.ordersSummary.orders || [],
		loading,
		loadMore
	};
};

type ProjectReturnsResult = {
	loading: boolean;
	returns: ProjectReturn[];
	loadMore: () => void;
};

/**
 * Gets returns for a project
 *
 * @param projectId - filter returns to those attached to this project id
 */
export const useProjectReturns = (projectId?: number): ProjectReturnsResult => {
	const [, setCurrentPage] = useState(1);

	const inputParams: ProjectReturnsQueryVariables['input'] = getOrdersReturnsInput(projectId);

	const { data, loading, fetchMore } = useQuery<ProjectReturnsQuery, ProjectReturnsQueryVariables>(PROJECT_RETURNS, {
		variables: {
			input: inputParams
		}
	});

	const loadMore = () => loadMoreOrdersReturns(setCurrentPage, fetchMore, inputParams);

	return {
		returns: data?.returnsSummary.returns || [],
		loading,
		loadMore
	};
};
